'use client';

import SectionCard from '@/app/(website)/components/SectionCard';
import SectionHeader from '@/app/(website)/components/SectionHeader';
import CheckCircle from '@/assets/icons/CheckCircle.svg';
import Clock from '@/assets/icons/Clock.svg';
import Flash from '@/assets/icons/Flash.svg';
import Rocket from '@/assets/icons/Rocket.svg';
import { Body } from '@/components/body';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

export default function HighlightsCondominio() {
  const [isVisible, setIsVisible] = useState(false);
  const gridRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.2,
      },
    );

    if (gridRef.current) {
      observer.observe(gridRef.current);
    }

    return () => {
      if (gridRef.current) {
        observer.unobserve(gridRef.current);
      }
    };
  }, []);

  return (
    <div
      id="highlights"
      className="flex flex-col items-center gap-10 py-16 px-8"
    >
      <SectionHeader
        badgeText="Funzionalità"
        title={
          <>
            Riduci il tempo operativo,{' '}
            <span className="rounded-[2rem] rounded-br-[8px] py-0 px-2 md:px-4 bg-secondary text-primary">
              aumenta
            </span>{' '}
            la conformità normativa
          </>
        }
        subtitle="Con Normo.ai, l'Amministratore di Condominio ha un supporto concreto nella ricerca normativa, nell'analisi dei documenti dello studio e nella costruzione dei documenti tipici della professione come lettere, delibere e verifiche tecniche."
      />
      <div
        className="grid grid-cols-1 md:grid-cols-2 max-w-[56.625rem] gap-8"
        ref={gridRef}
      >
        <SectionCard
          text="Ottieni risposte complete e argomentate, con riferimenti puntuali alla normativa italiana ed europea, alla prassi e alla giurisprudenza rilevante in materia condominiale, incluse disposizioni attuative, leggi speciali su impianti e antincendio, e sentenze della Cassazione. Uno strumento per affrontare contestazioni, dubbi interpretativi e decisioni operative con basi solide e documentate."
          title="Risposte con fonti rilevanti"
          icon={<Flash width={64} height={64} />}
          className={`transition-all duration-1000 ease-in-out transform ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-40'
          }`}
        />
        <SectionCard
          text="Resta sempre allineato all'evoluzione delle normative edilizie, fiscali (bonus e detrazioni) e giurisprudenziali, senza dover inseguire manualmente ogni nuova sentenza o circolare dell'Agenzia delle Entrate."
          title="Fonti aggiornate quotidianamente"
          icon={<Clock width={64} height={64} />}
          className={`transition-all duration-1000 ease-in-out transform delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-40'
          }`}
        />
        <SectionCard
          text="Dalla bozza di verbale assembleare alla lettera di diffida per morosità, fino al controllo delle clausole di un contratto d'appalto: Normo.ai accelera la fase preparatoria e amministrativa che toglie tempo alla gestione sul campo."
          title="Analisi, redazione e revisione documentale"
          icon={<CheckCircle width={64} height={64} />}
          className={`transition-all duration-1000 ease-in-out transform delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-40'
          }`}
        />
        <SectionCard
          text="Non uno strumento limitato alla sola contabilità e la gestione dei guasti, ma un supporto unico quando nel caso condominiale concreto si intrecciano profili legali, fiscali, tecnici e di mediazione civile."
          title="Gestione trasversale del cittadino, non a silos"
          icon={<Rocket width={64} height={64} />}
          className={`transition-all duration-1000 ease-in-out transform delay-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-40'
          }`}
        />
      </div>
      <div
        className={cn(
          'flex flex-col items-center md:flex-row md:items-start gap-4 max-w-[912px] transition-all duration-1000 ease-in-out transform delay-1000',
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-40',
        )}
      >
        <Image
          src="/ec.png"
          alt="Certificazion Euroconference"
          width={100}
          height={100}
        />
        <div className="flex flex-col">
          <Body variant="small">Certificazione Autorale Euroconference</Body>
          <Body variant="small" className="font-normal">
            Il modello di intelligenza artificiale generativa di Normo.ai è
            sottoposto ad un processo continuo di verifica e validazione da
            parte di un Comitato di Esperti Euroconference che contribuisce a
            valutarne la correttezza, la completezza e il rigore dei contenuti.
            Tale processo di controllo qualità ha l’obiettivo di offrire ai
            professionisti elaborati qualificati, attendibili e coerenti con
            l’evoluzione normativa, fermo restando il necessario giudizio
            critico del professionista.
          </Body>
        </div>
      </div>
    </div>
  );
}
